import { PermissionsUtil, StringUtil} from "@mcleod/core";
import {
    ClickEvent, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, EditRowDecorator, Label, Layout,
    ValidationResult
} from "@mcleod/components";
import { CommonTooltips } from "@mcleod/common";
import { AutogenLayoutDriverManagementExpand } from "./autogen/AutogenLayoutDriverManagementExpand";
import { ExtraPaySlideout } from "../../asset/ExtraPaySlideout";
import { PlanningUtil } from "../PlanningUtil";
import { MultiStop } from "../../MultiStop";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";

export class DriverManagementExpand extends AutogenLayoutDriverManagementExpand {
    onLoad(): void | Promise<void> {
        this.buttonSettlementDetails.visible = !PermissionsUtil.isUserDeniedLayout("mcleod-api-lme/lme/dispatch/asset/ExtraPay");
        this.buttonSettlementDetails.visible = !PermissionsUtil.isUserDeniedLayout("lme/dispatch/asset/ExtraPay");
        this.textboxStopsPickDrop.printableLabel.addUnderlineWhenMouseOver();
        this.textboxStopsPickDrop.addClickListener(() => this.textboxStopsOnClick());
    }

    sourceDriverManagerOnDisplay() {
        this.setUpOriginOrderIdLabel();
        this.setupManifestData();
    }

    private setUpOriginOrderIdLabel() {
        const modelRow = this.mainDataSource.activeRow;
        if (modelRow.isNull("origin_manifest_id") && !modelRow.isNull("origin_order_id")) {
            this.labelOriginOrderId.addUnderlineWhenMouseOver();
            this.labelOriginOrderId.tooltip = "Click to view order";
        }
        if (modelRow.isNull("origin_order_id"))
            this.labelOriginOrderId.visible = false;
    }

    private setupManifestData() {
        const modelRow = this.mainDataSource.activeRow;
        if (!modelRow.isNull("manifest_id") || !modelRow.isNull("origin_manifest_id")) {
            this.labelManifestId.visible = true;
            this.labelManifestId.addUnderlineWhenMouseOver();
            this.labelManifestId.tooltip = "Click to view manifest";
            this.labelOriginOrderId.visible = false;
            this.textboxCommodity.visible = false;
            this.textboxOrderValue.visible = false;
            this.labelContactName.visible = false;
            this.labelContactPhone.visible = false;
            this.labelContactEmail.visible = false;
            this.textboxTrailerType.visible = false;
            this.textboxOpsUserName.visible = false;
            this.textboxCustName.visible = false;
            this.citystateCust.visible = false;
            if (modelRow.isNull("origin_manifest_id")) {
                modelRow.set("origin_manifest_id", modelRow.get("manifest_id"));
            }
        }
        else {
            this.textboxManifestCommodities.visible = false;
            this.textboxManifestContactIds.visible = false;
            this.textboxManifestCustomers.visible = false;
            this.textboxManifestOperationsUsers.visible = false;
            this.textboxManifestTrailerTypes.visible = false;
            this.textboxManifestOrderValue.visible = false;
            this.labelManifestId.visible = false;
        }
    }

    labelOriginOrderIdOnClick(event: ClickEvent) {
        PlanningUtil.navigateToOrder(event);
    }

    refreshEmptyMoveState() {
        const modelRow = this.mainDataSource.activeRow;
        if (modelRow?.getBoolean("emptied", false)) {
            const hasManifestId = !StringUtil.isEmptyString(modelRow.get("origin_manifest_id", null));
            const hasOrderId = !StringUtil.isEmptyString(modelRow.get("origin_order_id", null));
            const showComponent = hasManifestId || hasOrderId;
            this.panelCustomerDetails.visible = showComponent;
            this.labelEmptyMove.visible = !showComponent;
        }
    }

    setUpStopsTextbox(){
        const movementId = this.sourceDriverManager.activeRow?.get("np_move_id") || this.sourceDriverManager.activeRow?.get("movement_id");
        if (movementId != null) {
            CommonTooltips.setTooltipContentLayout(this.textboxStopsPickDrop, null, "lme/dispatch/StopAppointmentStatusList",
                { maxWidth: 800, maxHeight: 600 }, { movement_id: movementId });
        }
    }

    setTooltipContent() {
        CommonTooltips.setTooltipContentLayout(this.panelCustomerInfo, this.sourceDriverManager.activeRow?.get("customer_id"), "lme/ar/CustomerQuickInfo");
        CommonTooltips.setTooltipContentLayout(this.panelOriginLocation, this.sourceDriverManager.activeRow?.get("origin_stop_id"), "lme/dispatch/planning/PlanningLocationQuickInfo");
        CommonTooltips.setTooltipContentLayout(this.panelDestination, this.sourceDriverManager.activeRow?.get("destination_stop_id"), "lme/dispatch/planning/PlanningLocationQuickInfo");
    }

    /** This is an event handler for the afterExecution event of sourceEquipmentPositionsAndCallins. */
    sourceEquipmentPositionsAndCallinsAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            const actualArrival = this.activeRow.get("actual_arrival", null);
            const actualDeparture = this.activeRow.get("actual_departure", null);
            if (actualArrival != null || actualDeparture != null) {
                this.labelStatus.backgroundColor = actualArrival != null && actualDeparture != null ? "warning" : "success";
                this.labelStatus.text = actualArrival != null && actualDeparture != null ? "Departed" : "Arrived";
            }
            this.labelStopType.text = this.sourceEquipmentPositionsAndCallins.activeRow.get("stop_type") == "PU" ? "PICKUP" : "DELIVERY";
            CommonTooltips.setTooltipContentLayout(this.panelLocation, this.sourceEquipmentPositionsAndCallins.activeRow.get("stop_id"), "lme/dispatch/planning/PlanningLocationQuickInfo", { minWidth: 128, minHeight: 140 });
        }
    }

    setupTemp(){
        const isManifest = !this.sourceDriverManager.activeRow.isNull("manifest_id");
        const minTemp = isManifest? this.sourceDriverManager.activeRow?.get("manifest_min_temp")
            : this.sourceDriverManager.activeRow?.get("min_temp");
        const maxTemp = isManifest? this.sourceDriverManager.activeRow?.get("manifest_max_temp")
            : this.sourceDriverManager.activeRow?.get("max_temp");
        if(!StringUtil.isEmptyString(minTemp) && !StringUtil.isEmptyString(maxTemp))
            this.textboxTemp.text = minTemp + '\u00B0' + maxTemp + '\u00B0';
    }

    displayArriveLateTime() {
        const originEarly = this.sourceDriverManager.activeRow.getDateTime("origin_early");
        const originLate = this.sourceDriverManager.activeRow.getDateTime("origin_late");
        this.labelOriginWindow.text = PlanningUtil.formatDateWindow(originEarly, originLate);

        const destEarly = this.sourceDriverManager.activeRow.getDateTime("destination_early");
        const destLate = this.sourceDriverManager.activeRow.getDateTime("destination_late");
        this.labelDestinationWindow.text = PlanningUtil.formatDateWindow(destEarly, destLate);
    }

    textboxStopsOnClick() {
        const row = this.activeRow;
        const isManifest = row.get("origin_manifest_id") != null || row.get("manifest_id") != null;
        const manifestId = isManifest ? row.get("origin_manifest_id") || row.get("manifest_id") : null;
        const movementId = isManifest ? row.get("np_move_id") || row.get("movement_id") : null;
        const searchInput = isManifest ? { manifest_id: manifestId, movement_id: movementId } : { id: row.get("origin_order_id") };
        let multiStopLayout: MultiStop;
        if (row) {
            new EditRowDecorator({
                title: isManifest ? `Stop Details - Manifest ${manifestId}` : `Stop Details - Order ${row.get("order_id")}`,
                layout: multiStopLayout = Layout.getLayout("lme/dispatch/MultiStop") as MultiStop,
                layoutLoadListeners: async () => {
                    if (isManifest) {
                        multiStopLayout.mainDataSource = multiStopLayout.sourceManifestMultistop;
                        multiStopLayout.sourceStop.parentDataSource = multiStopLayout.sourceManifestMultistop;
                        multiStopLayout.sourceStop.orderBy = [{ field: "movement_sequence", sort: "asc" }];
                        multiStopLayout.isManifest = true;
                    }
                    await multiStopLayout.mainDataSource.search(searchInput);
                    multiStopLayout.mainDataSource.mode = DataSourceMode.UPDATE;
                    multiStopLayout.activeRow?.set("order_id", row.get("origin_order_id"));
                    const summaryData = multiStopLayout.mainDataSource.summaryData;
                    PlanningUtil.setCommodityTooltipContent(multiStopLayout.textboxCommodityId, multiStopLayout.activeRow);
                if (isManifest) {
                    multiStopLayout.panelOrderInfo.visible = false;
                    multiStopLayout.panelManifestInfo.visible = true;
                }
                if (isManifest && summaryData?.get("customers").length === 1) {
                    multiStopLayout.labelManifestCustAddress1.visible = true;
                    multiStopLayout.citystateManifestCust.visible = true;
                }
                },
                width: '80%',
                overlayProps: { closeOnClickOff: false, greyedBackground: true },
                onSave: async () => {
                    await multiStopLayout.postChanges();
                },
                onClose: () => multiStopLayout.slideOut(),
                onValidate: () => {
                    const validationResults:ValidationResult[] = multiStopLayout.validate(true, true);
                    if (!ValidationUtil.checkValidationResults(validationResults)) {
                        ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid));
                    }
                    return validationResults;
                }
            });
        }
    }

    async buttonSettlementDetailsOnClick() {
        const sourceMovement: DataSource = new DataSource({ id: "sourceMovement", url: "lme/dispatch/movement" });
        sourceMovement.layoutName = "lme/dispatch/Movement";
        const sourceEquipmentGroup: DataSource = new DataSource({ id: "sourceEquipmentGroup", url: "lme/dispatch/equipment-group" });
        sourceEquipmentGroup.layoutName = "lme/dispatch/Movement";
        await sourceMovement.search({ id: this.sourceDriverManager.activeRow.get("movement_id") });
        await sourceEquipmentGroup.search({ id: this.sourceDriverManager.activeRow.get("equip_group_id") });
        const slideout = new ExtraPaySlideout(sourceMovement, sourceEquipmentGroup);
        slideout.showSlideout((canceled: boolean) => {
            if (!canceled)
                sourceMovement.search({ id: this.sourceDriverManager.activeRow.get("movement_id") });
                sourceEquipmentGroup.search({ id: this.sourceDriverManager.activeRow.get("equip_group_id") });
        });
    }

    /** This is an event handler for the onClick event of labelManifestId. */
    labelManifestIdOnClick(event: ClickEvent) {
        const manifestId = (event.target as Label).getDataValue();
        PlanningUtil.navigateToManifest(manifestId);
    }
}
